import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  IconButton, 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText,
  Tooltip,
  Box,
  Typography,
  Chip,
  useMediaQuery,
  useTheme,
  Select
} from '@mui/material';
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { AlertDialog } from './';

import { SUPPORTED_LANGUAGES, getLanguageByCode } from '../../constants';

const LanguageSelector = ({ onAnalyzePage = false }) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  // ✅ 언어 변경 모달 상태
  const [languageChangeModal, setLanguageChangeModal] = useState({
    open: false,
    newLanguage: ''
  });

  // 🔥 URL 파라미터로 전달된 언어 변경 모달 처리
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const showLanguageModal = urlParams.get('showLanguageModal');
    const newLanguageName = urlParams.get('newLanguage');
    
    if (showLanguageModal === 'true' && newLanguageName) {
      // 모달 표시
      setLanguageChangeModal({
        open: true,
        newLanguage: decodeURIComponent(newLanguageName)
      });
      
      // URL에서 파라미터 제거
      const newUrl = location.pathname;
      window.history.replaceState(null, '', newUrl);
    }
  }, [location]);

  const currentLanguage = getLanguageByCode(i18n.language);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = async (languageCode) => {
    try {
      console.log(`🌍 [언어 변경] ${currentLanguage.code} → ${languageCode}`);
      
      // 메뉴 닫기
      handleClose();
      
      // analyze 페이지에서 언어 변경 시 랜딩페이지로 이동
      if (onAnalyzePage || location.pathname.includes('/analyze')) {
        // 언어 변경 후 랜딩페이지로 이동 (URL 파라미터로 모달 정보 전달)
        await i18n.changeLanguage(languageCode);
        
        const newLanguage = getLanguageByCode(languageCode);
        navigate(`/?showLanguageModal=true&newLanguage=${encodeURIComponent(newLanguage.nativeName)}`);
        
      } else {
        // 랜딩페이지에서는 기존 동작 유지
        await i18n.changeLanguage(languageCode);
        
        const newLanguage = getLanguageByCode(languageCode);
        setLanguageChangeModal({
          open: true,
          newLanguage: newLanguage.nativeName
        });
      }
      
      console.log(`✅ [언어 변경] 완료: ${languageCode}`);
    } catch (error) {
      console.error('❌ [언어 변경] 실패:', error);
    }
  };

  // ✅ 언어 변경 모달 닫기
  const handleLanguageChangeModalClose = () => {
    setLanguageChangeModal({
      open: false,
      newLanguage: ''
    });
  };

  // 모바일에서는 약어, 데스크톱에서는 전체 이름
  const displayText = isMobile 
    ? currentLanguage.code.toUpperCase() 
    : currentLanguage.nativeName;

  // 모바일에서는 고정 width 80px
  const chipWidth = isMobile ? 80 : 120;
  const chipMaxWidth = isMobile ? 80 : 140;

  return (
    <Box>
      <Tooltip title={t('common.selectLanguage')}>
        <Chip
          onClick={handleClick}
          avatar={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                fontSize: '0.9em'
              }}
            >
              {currentLanguage.flag}
            </Box>
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: isMobile ? 30 : 80, width: '100%' }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flex: 1
                }}
              >
                {displayText}
              </Typography>
              <ArrowDownIcon sx={{ fontSize: isMobile ? '0.875rem' : '1rem', flexShrink: 0 }} />
            </Box>
          }
          variant="outlined"
          size="small"
          sx={{
            height: 32,
            width: chipWidth,
            maxWidth: chipMaxWidth,
            borderColor: 'rgba(0, 0, 0, 0.23)',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              borderColor: 'rgba(0, 0, 0, 0.5)'
            },
            '& .MuiChip-label': {
              px: isMobile ? 0.5 : 1
            },
            '& .MuiChip-avatar': {
              ml: 0.5,
              mr: isMobile ? -0.25 : -0.5
            }
          }}
        />
      </Tooltip>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            minWidth: isMobile ? 180 : 200,
            maxWidth: isMobile ? 200 : 220,
            mt: 1
          }
        }}
      >
        {SUPPORTED_LANGUAGES.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            selected={language.code === currentLanguage.code}
            sx={{
              py: 1.5,
              '&.Mui-selected': {
                backgroundColor: 'rgba(25, 118, 210, 0.12)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.2)'
                }
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <span style={{ fontSize: '1.2em' }}>{language.flag}</span>
            </ListItemIcon>
            <ListItemText 
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>{language.nativeName}</span>
                  {isMobile && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                      {language.code.toUpperCase()}
                    </Typography>
                  )}
                </Box>
              }
              sx={{
                '& .MuiListItemText-primary': {
                  fontWeight: language.code === currentLanguage.code ? 600 : 400,
                  whiteSpace: 'nowrap'
                }
              }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* ✅ 언어 변경 확인 모달 */}
      <AlertDialog
        open={languageChangeModal.open}
        onClose={handleLanguageChangeModalClose}
        type="success"
        title=""
        message={t('common.languageChanged', { language: languageChangeModal.newLanguage })}
        confirmText={t('common.confirm')}
      />
    </Box>
  );
};

export default LanguageSelector; 